import { queryOptions, type UseQueryResult, useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { startOfDay } from "date-fns";
import { availabilityApiService } from "../services/availabilityApiService";
import {
  type Availability,
  type AvailabilityOverride,
  type CreateAvailabilityOverrideRequest,
  type CreateAvailabilityRequest,
  fromAvailabilityDtoToAvailability,
  fromAvailabilityOverrideDtoToAvailabilityOverride,
  fromCreateAvailabilityOverrideRequestToCreateAvailabilityOverrideDto,
  fromCreateAvailabilityRequestToCreateAvailabilityDto,
  fromTimeSlotDtoToTimeSlot,
  fromUpdateAvailabilityRequestToUpdateAvailabilityDto,
  type TimeSlot,
  type UpdateAvailabilityRequest,
} from "../types/availabilityTypes";

const staleTime = 5 * 60 * 1000; // 5 minutes

export const availabilityKeys = {
  all: ["availability"] as const,
  lists: () => [...availabilityKeys.all, "list"] as const,
  list: (filters: {
    userId?: string;
    businessId?: string;
    locationId?: string;
  }) => [...availabilityKeys.lists(), filters] as const,
  details: () => [...availabilityKeys.all, "detail"] as const,
  detail: (id: string) => [...availabilityKeys.details(), id] as const,
  overrides: () => [...availabilityKeys.all, "overrides"] as const,
  overridesList: (filters: {
    userId?: string;
    businessId?: string;
    locationId?: string;
    effectiveFrom?: string;
    effectiveTo?: string;
    isActive?: boolean;
  }) => [...availabilityKeys.overrides(), filters] as const,
};

export const useAvailabilities = (params?: {
  userId: string;
  businessId: string;
  locationId: string;
}) => {
  return useQuery<Availability[], Error>({
    queryKey: availabilityKeys.list(params || {}),
    queryFn: async ({ signal }) => {
      const dtos = await availabilityApiService.getAll(params, signal);
      return dtos.map(fromAvailabilityDtoToAvailability);
    },
    staleTime,
    enabled: !!params?.userId && !!params?.businessId && !!params?.locationId,
  });
};

export const useCreateAvailability = () => {
  const queryClient = useQueryClient();

  return useMutation<Availability, Error, CreateAvailabilityRequest>({
    meta: { action: "availability.create" },
    mutationFn: async (data: CreateAvailabilityRequest) => {
      const dto = fromCreateAvailabilityRequestToCreateAvailabilityDto(data);
      const resultDto = await availabilityApiService.create(dto);
      return fromAvailabilityDtoToAvailability(resultDto);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: availabilityKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ["availableTimeSlots"] });
    },
  });
};

export const useUpdateAvailability = (availabilityId: string) => {
  const queryClient = useQueryClient();

  return useMutation<Availability, Error, Partial<UpdateAvailabilityRequest>>({
    meta: { action: "availability.update" },
    mutationFn: async (data: Partial<UpdateAvailabilityRequest>) => {
      const dto = fromUpdateAvailabilityRequestToUpdateAvailabilityDto(
        data as UpdateAvailabilityRequest
      );
      const resultDto = await availabilityApiService.update(
        availabilityId,
        dto
      );
      return fromAvailabilityDtoToAvailability(resultDto);
    },
    onSuccess: (availability) => {
      queryClient.setQueryData(
        availabilityKeys.detail(availability.id),
        availability
      );
      queryClient.invalidateQueries({ queryKey: availabilityKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ["availableTimeSlots"] });
    },
  });
};

export const useDeleteAvailability = (availabilityId: string) => {
  const queryClient = useQueryClient();

  return useMutation<{ message: string }, Error>({
    meta: { action: "availability.delete" },
    mutationFn: () => availabilityApiService.delete(availabilityId),
    onSuccess: () => {
      queryClient.removeQueries({
        queryKey: availabilityKeys.detail(availabilityId),
      });
      queryClient.invalidateQueries({ queryKey: availabilityKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ["availableTimeSlots"] });
    },
  });
};

type AvailabilityScope = {
  userId: string;
  businessId: string;
  locationId: string;
};

const overridesQueryOptions = (params?: AvailabilityScope & {
  startDate: Date | string;
  endDate: Date | string;
}) => {
  const dateParameter = (date: Date | string | undefined) =>
    typeof date === "string" ? date : date ? startOfDay(date).toISOString() : "";

  const queryParams = {
    userId: params?.userId || "",
    businessId: params?.businessId || "",
    locationId: params?.locationId || "",
    startDate: dateParameter(params?.startDate),
    endDate: dateParameter(params?.endDate),
  };

  return queryOptions<AvailabilityOverride[], Error>({
    queryKey: availabilityKeys.overridesList(queryParams),
    queryFn: async ({ signal }) => {
      const dtos = await availabilityApiService.getAllOverrides(queryParams, signal);
      return dtos.map(fromAvailabilityOverrideDtoToAvailabilityOverride);
    },
    staleTime,
    enabled: !!params?.userId && !!params?.businessId && !!params?.locationId,
  });
};

export const useAvailabilityOverrides = (params?: AvailabilityScope & {
  startDate: Date | string;
  endDate: Date | string;
  isActive?: boolean;
}) => useQuery(overridesQueryOptions(params));

export const useAvailabilityOverridesForRanges = (
  scope: AvailabilityScope,
  ranges: { startDate: string; endDate: string }[],
) => combineRangeQueries(useQueries({
  queries: ranges.map((range) => overridesQueryOptions({ ...scope, ...range })),
}));

export const useCreateAvailabilityOverride = () => {
  const queryClient = useQueryClient();

  return useMutation<
    AvailabilityOverride,
    Error,
    CreateAvailabilityOverrideRequest
  >({
    meta: { action: "availability_override.create" },
    mutationFn: async (data: CreateAvailabilityOverrideRequest) => {
      const dto =
        fromCreateAvailabilityOverrideRequestToCreateAvailabilityOverrideDto(
          data
        );
      const resultDto = await availabilityApiService.createOverride(dto);
      return fromAvailabilityOverrideDtoToAvailabilityOverride(resultDto);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: availabilityKeys.overrides() });
      queryClient.invalidateQueries({ queryKey: ["availableTimeSlots"] });
    },
  });
};

export const useDeleteAvailabilityOverride = (overrideId: string) => {
  const queryClient = useQueryClient();

  return useMutation<{ message: string }, Error>({
    meta: { action: "availability_override.delete" },
    mutationFn: () => availabilityApiService.deleteOverride(overrideId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: availabilityKeys.overrides() });
      queryClient.invalidateQueries({ queryKey: ["availableTimeSlots"] });
    },
  });
};

const timeSlotsQueryOptions = (params?: AvailabilityScope & {
  startAt: Date;
  endAt: Date;
  excludeAppointmentId?: string;
}) => {
  const queryParams = {
    userId: params?.userId || "",
    businessId: params?.businessId || "",
    locationId: params?.locationId || "",
    startAt: params?.startAt.toISOString() || "",
    endAt: params?.endAt.toISOString() || "",
    excludeAppointmentId: params?.excludeAppointmentId,
  };

  return queryOptions<TimeSlot[], Error>({
    queryKey: ["availableTimeSlots", queryParams],
    queryFn: async ({ signal }) => {
      const dtos = await availabilityApiService.getTimeSlots(queryParams, signal);
      return dtos.map((dto) => fromTimeSlotDtoToTimeSlot(dto));
    },
    staleTime,
    enabled: !!params?.userId && !!params?.businessId && !!params?.locationId,
  });
};

export const useAvailableTimeSlots = (params?: AvailabilityScope & {
  startAt: Date;
  endAt: Date;
  excludeAppointmentId?: string;
}) => useQuery(timeSlotsQueryOptions(params));

export const useAvailableTimeSlotsForRanges = (
  scope: AvailabilityScope,
  ranges: { startAt: Date; endAt: Date }[],
) => combineRangeQueries(useQueries({
  queries: ranges.map((range) => timeSlotsQueryOptions({ ...scope, ...range })),
}));

function combineRangeQueries<T>(queries: UseQueryResult<T, Error>[]) {
  return {
    data: queries.map((query) => query.data),
    error: queries.find((query) => query.error)?.error ?? null,
    isLoading: queries.some((query) => query.isLoading),
    isFetching: queries.some((query) => query.isFetching),
    refetch: () => Promise.all(queries.map((query) => query.refetch())),
  };
}
